// ---------------------------------------------------------------------------
// newsletter — footer MailChimp signup (replaces the legacy jQuery + parsley
// Newsletter.coffee flow).
//
// Markup contract (see partials/newsletter.html):
//   <form data-newsletter action="<mailchimp post url>" method="post"> … </form>
//   status node: <p data-newsletter-status>  (reflects state via data-state)
//
// MailChimp's classic /subscribe/post endpoint does not send CORS headers, so
// the response is unreadable from JS. We submit with a `no-cors` fetch (which
// still delivers the subscription) and optimistically show success; a network
// failure surfaces the error state. The form's native action/target is kept as
// a no-JS fallback. When settings.newsletterActionUrl is unset the partial
// renders a mailto link instead and this module finds no forms to enhance.
// ---------------------------------------------------------------------------

type NewsletterState = 'idle' | 'sending' | 'sent' | 'error';

const MESSAGES: Record<NewsletterState, string> = {
	idle: '',
	sending: 'Sending…',
	sent: 'Thank you. You are now subscribed.',
	error: 'Something went wrong. Please try again later.',
};

const setState = (
	form: HTMLFormElement,
	status: HTMLElement | null,
	state: NewsletterState,
): void => {
	form.dataset.state = state;
	if (status) status.textContent = MESSAGES[state];
	const submit = form.querySelector<HTMLButtonElement>('button[type="submit"]');
	if (submit) submit.disabled = state === 'sending';
};

const initForm = (form: HTMLFormElement): void => {
	const status = form.querySelector<HTMLElement>('[data-newsletter-status]');
	setState(form, status, 'idle');

	form.addEventListener('submit', (event) => {
		event.preventDefault();
		if (!form.checkValidity()) {
			form.reportValidity();
			return;
		}

		const action = form.getAttribute('action');
		if (!action) return;

		const body = new URLSearchParams();
		for (const [key, value] of new FormData(form).entries()) {
			if (typeof value === 'string') body.append(key, value);
		}

		setState(form, status, 'sending');

		fetch(action, {
			method: 'POST',
			mode: 'no-cors',
			headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
			body,
		})
			.then(() => {
				form.reset();
				setState(form, status, 'sent');
			})
			.catch(() => setState(form, status, 'error'));
	});
};

export const initNewsletter = (): void => {
	const forms = document.querySelectorAll<HTMLFormElement>('form[data-newsletter]');
	for (const form of forms) initForm(form);
};
